import { useState } from 'react';
import { getErrorMessage } from '@/services/api';

/**
 * #377: a masked identity number with a Reveal control.
 *
 * The control renders only when the server said this viewer may reveal
 * (`can_reveal_identity` on the record). Revealing is a server round trip so
 * that every full view is audited; the value is held in component state only
 * and is gone on Hide or when the page unmounts.
 */
export function RevealableIdentity({
  label,
  maskedValue,
  canReveal,
  onReveal,
}: {
  label: string;
  maskedValue: string;
  canReveal: boolean;
  onReveal: () => Promise<string>;
}) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReveal = async () => {
    setPending(true);
    setError(null);
    try {
      setRevealed(await onReveal());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="flex items-center gap-2 mt-0.5">
        <p className="text-sm font-medium text-gray-900 dark:text-white font-mono">{revealed ?? maskedValue}</p>
        {canReveal && maskedValue && (
          <button
            type="button"
            className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
            disabled={pending}
            onClick={revealed === null ? handleReveal : () => setRevealed(null)}
            aria-label={`${revealed === null ? 'Reveal' : 'Hide'} ${label}`}
          >
            {revealed === null ? (pending ? 'Revealing…' : 'Reveal') : 'Hide'}
          </button>
        )}
      </div>
      {error && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{error}</p>}
    </div>
  );
}
